// ─────────────────────────────────────────────────────────────────────────────
// client-identite.js — IDENTIFICATION d'une société : le numéro d'entreprise.
//
// Une société belge n'a qu'UNE identité opposable : son numéro d'entreprise BCE
// (10 chiffres). Le numéro de TVA est le MÊME numéro préfixé « BE ». Or la table
// `clients` le stocke dans TROIS colonnes selon le chemin d'écriture (`bce`,
// `vat`, `company_vat`) et dans des formats hétérogènes (« BE 1028.230.781 »,
// « 1028230781 », « BE1028230781 »).
//
// Conséquence constatée en prod (06/08/2026) : deux dossiers pour la MÊME
// société — « Saidiya Entreprises » et « SAIDIYA ENTREPRISES SNC », tous deux
// n° 1023074341. Le formulaire d'implémentation Phase 1 écrit le numéro saisi
// dans `company_vat` (champ « Numéro de TVA », obligatoire) alors que la
// résolution du client existant ne regardait QUE `bce` (souvent laissé vide) :
// aucune correspondance → création d'un second client. Le rapprochement par
// dénomination ne rattrapait rien (« Saidiya Entreprises » ≠ « SAIDIYA
// ENTREPRISES SNC », comparaison en égalité stricte).
//
// Ce module est la source unique de vérité pour « est-ce la même société ? ».
// Fonctions PURES (aucune I/O) → testables sans base.
// ─────────────────────────────────────────────────────────────────────────────

// Colonnes/clés susceptibles de porter le numéro d'entreprise, tous chemins
// d'écriture confondus (import Excel, formulaire web, création manuelle, BCE).
const CHAMPS_ENTREPRISE = ['bce', 'vat', 'company_vat', 'tva', 'siret_bce', 'numero_bce'];

/**
 * Normalise un numéro d'entreprise / TVA belge en sa forme canonique : 10 chiffres.
 * Tolère « BE 0123.456.789 », « BE0123456789 », « 0123456789 », « 123456789 »
 * (ancien format 9 chiffres, complété par le 0 de tête comme le fait la BCE).
 * Renvoie '' si la valeur n'est pas un numéro d'entreprise exploitable
 * (« à immatriculer », vide, numéro tronqué en cours de frappe…).
 * @param {any} v
 * @returns {string} 10 chiffres, ou ''
 */
export function normEntreprise(v) {
  const digits = String(v == null ? '' : v).replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 9) return '0' + digits;  // format pré-2008
  return '';
}

/**
 * Tous les numéros d'entreprise portés par un objet (ligne `clients` OU payload
 * société d'un import/formulaire), normalisés et dédupliqués.
 * Regarde aussi `data.bce` : certains dossiers anciens n'ont le numéro que là.
 * @param {object} obj
 * @returns {string[]}
 */
export function numerosEntreprise(obj) {
  if (!obj || typeof obj !== 'object') return [];
  const out = new Set();
  for (const k of CHAMPS_ENTREPRISE) {
    const n = normEntreprise(obj[k]);
    if (n) out.add(n);
  }
  const d = obj.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    for (const k of CHAMPS_ENTREPRISE) {
      const n = normEntreprise(d[k]);
      if (n) out.add(n);
    }
  }
  return [...out];
}

/**
 * Deux objets désignent-ils la même société ? VRAI dès qu'ils partagent un
 * numéro d'entreprise, quelle que soit la colonne qui le porte de chaque côté.
 * FAUX si l'un des deux n'a aucun numéro exploitable (on ne devine JAMAIS une
 * identité à partir du seul nom : « Saidiya Entreprises » et « SAIDIYA
 * ENTREPRISES SNC » peuvent être deux entités juridiques distinctes).
 */
export function memeEntreprise(a, b) {
  const na = numerosEntreprise(a);
  if (!na.length) return false;
  const nb = new Set(numerosEntreprise(b));
  return na.some((n) => nb.has(n));
}

/**
 * Retrouve, dans une liste de clients déjà en base, celui qui porte le même
 * numéro d'entreprise que la société entrante. C'est LE garde-fou anti-doublon :
 * tout chemin de création doit le consulter AVANT d'insérer.
 *
 * Les lignes supprimées (`deleted_at`) sont ignorées : un dossier mis à la
 * corbeille ne doit pas empêcher d'en recréer un.
 *
 * @param {Array<object>} clients  lignes `clients` (déjà scopées RLS par l'appelant)
 * @param {object} societe         payload société entrant
 * @returns {object|null}
 */
export function trouverClientParEntreprise(clients, societe) {
  const cibles = new Set(numerosEntreprise(societe));
  if (!cibles.size) return null;
  return (clients || []).find(
    (c) => c && !c.deleted_at && numerosEntreprise(c).some((n) => cibles.has(n)),
  ) || null;
}

/**
 * Regroupe une liste de clients par numéro d'entreprise et ne renvoie que les
 * groupes de 2+ (les doublons réels). Sert à l'audit des dossiers DÉJÀ créés
 * avant la pose du garde-fou — la correction (fusion) reste une décision
 * humaine : on signale, on ne fusionne jamais tout seul.
 * @returns {Array<{numero:string, clients:Array<object>}>}
 */
export function grouperDoublons(clients) {
  const par = new Map();
  for (const c of clients || []) {
    if (!c || c.deleted_at) continue;
    for (const n of numerosEntreprise(c)) {
      if (!par.has(n)) par.set(n, []);
      // Un client portant le même numéro dans 2 colonnes ne se compte qu'une fois.
      if (!par.get(n).some((x) => x.id === c.id)) par.get(n).push(c);
    }
  }
  return [...par.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([numero, list]) => ({ numero, clients: list }));
}

// ═══════════════════════════════════════════════════════════════════════════
// L'IDENTITÉ D'UN EMPLOYEUR NE SE DEVINE PAS — ELLE MANQUE OU ELLE EST LÀ
// ───────────────────────────────────────────────────────────────────────────
// Ajouté le 14/08/2026, après un balayage demandé par le fondateur (« s'il y a
// d'autres problèmes comme ça, dis-les tous »). Neuf endroits de l'app
// écrivaient la même ligne : `co?.name || 'Aureus'`, `|| ONSS_NUMBER`,
// `|| 'GEBABEBB'`. Chacune paraissait inoffensive isolément ; ensemble elles
// forment UNE règle non écrite — « si la donnée du client manque, mets celle
// d'Aureus ». Sur une Dimona, une DmfA, une fiche de paie ou un fichier
// bancaire, ça ne produit pas une valeur approximative : ça produit une
// DÉCLARATION AU NOM DE QUELQU'UN D'AUTRE.
//
// Mesuré en base le 14/08 (12 clients actifs) : 11 sur 12 portent un matricule
// ONSS — le douzième partait donc sous celui d'Aureus, en silence.
//
// ⚠️ Le repli est PIRE que l'absence, et c'est tout l'argument : sans repli le
// traitement s'arrête et quelqu'un corrige la fiche ; avec repli il réussit, le
// fichier est bien formé, personne ne regarde. `buildSEPAXML` l'avait déjà
// compris pour l'IBAN débiteur (« on refuse net plutôt que de deviner ») — mais
// pas pour le nom ni le BIC, dans la même fonction. Ce module généralise ce
// réflexe au lieu d'en inventer un second.
//
// Fonction PURE (aucune I/O) → testable sans base.
// ═══════════════════════════════════════════════════════════════════════════

/** Première valeur non vide parmi une liste de clés, `data` JSONB compris. */
function champ(obj, cles) {
  for (const k of cles) {
    const v = obj && obj[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const d = obj && obj.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    for (const k of cles) {
      const v = d[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

/**
 * Identité légale opposable d'un employeur, telle qu'elle est RÉELLEMENT
 * enregistrée. Ne substitue jamais rien : ce qui manque est nommé dans
 * `manque`, pour que l'appelant le dise à l'utilisateur au lieu de le combler.
 *
 * @param {object} co  ligne `clients` (ou payload société équivalent)
 * @param {{ exige?: string[] }} [opts]  champs requis pour CE traitement
 *        ('nom' | 'entreprise' | 'onss'). Défaut : nom + entreprise.
 * @returns {{ok:boolean, nom:string, entreprise:string, onss:string, manque:string[]}}
 */
export function identiteEmployeur(co, opts) {
  const exige = (opts && Array.isArray(opts.exige) && opts.exige.length)
    ? opts.exige
    : ['nom', 'entreprise'];
  const nom = champ(co, ['nom', 'name', 'raison_sociale', 'denomination']);
  // La BCE passe par normEntreprise : elle vit dans 3 colonnes et 3 formats.
  const entreprise = normEntreprise(numerosEntreprise(co)[0] || '');
  // Le matricule ONSS n'a pas de forme canonique publiée ici : on garde les
  // chiffres, sans les reformater — le générateur DmfA le fait déjà.
  const onss = champ(co, ['onss_number', 'onss', 'matricule_onss']).replace(/\D/g, '');
  const valeurs = { nom, entreprise, onss };
  const manque = exige.filter((k) => !valeurs[k]);
  return { ok: manque.length === 0, nom, entreprise, onss, manque };
}

/** Libellé lisible d'un champ manquant, pour un message d'erreur utilisateur. */
const LIBELLE_CHAMP = {
  nom: 'la dénomination sociale',
  entreprise: "le numéro d'entreprise (BCE)",
  onss: 'le matricule ONSS',
};

/**
 * Même chose, mais LÈVE une erreur nommée quand l'identité est incomplète.
 * À utiliser dans tout traitement qui produit une pièce opposable (déclaration,
 * fiche de paie, fichier bancaire) : mieux vaut un arrêt lisible qu'un document
 * juste-en-apparence émis au nom d'une autre société.
 *
 * @param {object} co
 * @param {{ pour: string, exige?: string[] }} opts  `pour` = ce qu'on allait produire
 * @returns {{nom:string, entreprise:string, onss:string}}
 */
export function exigerIdentiteEmployeur(co, opts) {
  const pour = (opts && opts.pour) || 'ce document';
  const id = identiteEmployeur(co, opts);
  if (!id.ok) {
    const quoi = id.manque.map((k) => LIBELLE_CHAMP[k] || k).join(' et ');
    const e = new Error(
      `Identité de l'employeur incomplète : ${quoi} manque${id.manque.length > 1 ? 'nt' : ''} `
      + `dans la fiche société. Complétez-la avant de générer ${pour} — `
      + `aucune valeur de remplacement n'est utilisée, pour ne jamais déclarer au nom d'une autre société.`
    );
    e.code = 'IDENTITE_EMPLOYEUR_INCOMPLETE';
    e.manque = id.manque;
    throw e;
  }
  return { nom: id.nom, entreprise: id.entreprise, onss: id.onss };
}
